import * as React from 'react';
import {useRef, useEffect} from 'react';
import {useConstant} from '@sail/engine';
import {css} from '../css';
import {view} from '../view';
import {transitionHeightAuto} from './transition';

function isEmptyValue(value: string): boolean {
  return !value || value === '0' || value === '0px';
}

const getStyles = (expanded: boolean, hasMinHeight: boolean) => {
  return css({
    overflow: expanded ? css.value('auto') : css.value('hidden'),
    scrollbarWidth: css.value('none'),
    opacity: expanded || hasMinHeight ? css.value('1') : css.value('0'),
    transform:
      expanded || hasMinHeight
        ? css.value('scale(1)')
        : css.value('translateY(-2px) scale(0.99)'),
    transition: css.value(
      'transform 200ms ease-in-out, opacity 200ms ease-in-out, height 200ms ease-in-out',
    ),
    willChange: css.value('height'),
    position: css.value('relative'),
    // fade styles
    ...(hasMinHeight &&
      !expanded && {
        '&::after': {
          content: css.value('""'),
          position: css.value('absolute'),
          bottom: css.value('-40px'),
          width: css.value('100%'),
          height: css.value('40px'),
          boxShadow: css.value(
            '0 15px 35px 0 rgba(60, 66, 87, .08), 0 5px 15px 0 rgba(0, 0, 0, .12)',
          ), // .Card--shadow--large
        },
      }),
    '&::-webkit-scrollbar': {
      display: css.value('none'),
    },
  });
};

export interface CollapsibleContainerProps {
  /**
   *
   * Minimum height to which the panel should collapse. Used for truncating long content.
   */
  minHeight?: string;
  /**
   *
   * Spread into the component from collapsibleProps returned from useCollapsible
   */

  id: string;
  /**
   *
   * Open state of Collapsible area. Spread into the component from collapsibleProps returned from useCollapsible
   */
  open: boolean;
  /**
   * @deprecated This prop is unused. Migrate to the non-deprecated CollapsibleContainer to use onBeforeMatch.
   */
  onBeforeMatch?: () => void;
}

// internal wrapping div on line 78 is required to properly animate
export const CollapsibleContainer =
  view.section.create<CollapsibleContainerProps>(
    ({minHeight = '0', open, id, ...props}, View) => {
      const collapsibleRef = useRef(null);

      const hasMinHeight = !isEmptyValue(minHeight);

      // Set the height on the first render to ensure the element renders
      // correctly even with JavaScript disabled (to support SSR).
      // After the first render, the transition controls the height.
      const initialStyles = useConstant(() =>
        open
          ? {}
          : ({
              height: minHeight,
              visibility: hasMinHeight ? 'visible' : 'hidden',
            } as const),
      );

      useEffect(() => {
        if (collapsibleRef.current) {
          return transitionHeightAuto(collapsibleRef.current, open, minHeight);
        }
      }, [collapsibleRef, open, minHeight]);

      return (
        <View
          ref={collapsibleRef}
          role="region"
          id={id}
          uses={[props, getStyles(open, hasMinHeight)]}
          style={initialStyles}
        >
          <div>{props.children}</div>
        </View>
      );
    },
  );
